import firebase from "firebase/compat/app";
import "firebase/compat/database";

export interface BrandSetupData {
  setupMethod: "fetch" | "manual";
  brandUrl?: string;
  brandfetchResponse?: any;
  selectedAssets?: {
    useBrandName: boolean;
    useDescription: boolean;
    useLogos: boolean;
    useColors: boolean;
    useLinks: boolean;
    useFonts?: boolean;
    useFavicon?: boolean;
    useImages?: boolean;
  };
  createdAt: number;
  updatedAt: number;
  status: "in_progress" | "completed";
  progressTracker?: "brand_added" | "assets_selected" | "publish_started" | "customization_started" | "customization_completed" | "published";
  wlwSubdomain?: string;
  publishedAt?: number;
  newsfeedCustomized?: boolean;
  newsfeedLastSaved?: number;
  givingCustomized?: boolean;
  givingLastSaved?: number;
  lastCustomizedPage?: 'newsfeed' | 'giving';
}

export const saveBrandSetupData = async (
  charityId: string,
  data: Partial<BrandSetupData>
): Promise<void> => {
  if (!charityId) {
    throw new Error("Charity ID is required");
  }

  const database = firebase.database();
  const brandSetupRef = database.ref(`${charityId}/charitySetup/brandSetup`);

  const now = Date.now();
  
  const snapshot = await brandSetupRef.once('value');
  const existingData = snapshot.val() || {};

  const updatedData: Partial<BrandSetupData> = {
    ...existingData,
    ...data,
    updatedAt: now,
  };

  if (!existingData.createdAt) {
    updatedData.createdAt = now;
    updatedData.status = "in_progress";
  }

  await brandSetupRef.set(updatedData);
};

export const loadBrandSetupData = async (
  charityId: string
): Promise<BrandSetupData | null> => {
  if (!charityId) {
    throw new Error("Charity ID is required");
  }

  const database = firebase.database();
  const brandSetupRef = database.ref(`${charityId}/charitySetup/brandSetup`);

  const snapshot = await brandSetupRef.once('value');
  return snapshot.val() || null;
};

export const updateBrandSetupStatus = async (
  charityId: string,
  status: "in_progress" | "completed"
): Promise<void> => {
  await saveBrandSetupData(charityId, { status });
};

export interface BrandSetupStepIndices {
  customizeStepIndex: number;
}

export const getBrandSetupStepIndices = (
  isManualSetup: boolean,
  _usesExternalGivingPage = false
): BrandSetupStepIndices => {
  const customizeStepIndex = isManualSetup ? 1 : 2;

  return { customizeStepIndex };
};

export const determineStartingStep = (
  data: BrandSetupData | null,
  usesExternalGivingPage = false
): number => {
  if (!data || !data.progressTracker) {
    return 0;
  }

  const isManual = data.setupMethod === "manual";
  const { customizeStepIndex } = getBrandSetupStepIndices(
    isManual,
    usesExternalGivingPage
  );
  const assetsStepIndex = isManual ? -1 : 1;

  switch (data.progressTracker) {
    case "published":
    case "customization_completed":
    case "customization_started":
    case "publish_started":
      return customizeStepIndex;
    case "assets_selected":
      return customizeStepIndex;
    case "brand_added":
      if (isManual) {
        return customizeStepIndex;
      }
      return assetsStepIndex;
    default:
      return 0;
  }
};

export const transformToFormData = (data: BrandSetupData | null): any => {
  if (!data) return {};

  return {
    brandSetupMethod: data.setupMethod,
    brandUrl: data.brandUrl || "",

    useBrandName: data.selectedAssets?.useBrandName ?? true,
    useDescription: data.selectedAssets?.useDescription ?? true,
    useLogos: data.selectedAssets?.useLogos ?? true,
    useColors: data.selectedAssets?.useColors ?? true,
    useLinks: data.selectedAssets?.useLinks ?? true,
    useFavicon: data.selectedAssets?.useFavicon ?? true,
    useFonts: data.selectedAssets?.useFonts ?? true,
    useImages: data.selectedAssets?.useImages ?? true,
  };
};